import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MemberSearch } from '@/components/shared/search/MemberSearch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { AddRequest } from '@/types';
import { useFamilyStore } from '@/store/familyStore';
import { getAncestorLineage } from '@/utils/family/ancestors';

interface RequestDialogProps {
  request: AddRequest;
  open: boolean;
  onClose: () => void;
  onApprove: (requestId: string, updates: Partial<AddRequest>) => void;
}

export function RequestDialog({
  request,
  open,
  onClose,
  onApprove,
}: RequestDialogProps) {
  const { members } = useFamilyStore();
  const [formData, setFormData] = useState({
    childName: request.childName,
    parentId: request.parentId || '',
  });

  const requesterMember = members.find(m => m.name === request.requesterName);
  const requesterLineage = requesterMember ? getAncestorLineage(requesterMember.id, members) : request.requesterName;
  const currentParentLineage = formData.parentId ? 
    getAncestorLineage(formData.parentId, members) : '';

  const handleSubmit = () => {
    onApprove(request.id, {
      childName: formData.childName,
      parentId: formData.parentId,
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>مراجعة طلب الإضافة</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          <div>
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-2 items-center text-right">
                <span className="text-sm font-medium text-gray-600 justify-self-start">مقدم الطلب:</span>
                <span className="text-sm text-gray-900">{requesterLineage}</span>
                <span className="text-sm font-medium text-gray-600 justify-self-start">رقم الهاتف:</span>
                <div className="text-sm text-gray-900 text-right" dir="ltr">
                  <span className="inline-block">{request.phoneNumber}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="childName">اسم المولود</Label>
              <Input
                id="childName"
                value={formData.childName}
                onChange={(e) => setFormData(prev => ({ ...prev, childName: e.target.value }))}
                placeholder="أدخل اسم المولود"
              />
            </div>

            <div className="space-y-2">
              <Label>الأب</Label>
              {currentParentLineage && (
                <div className="mb-2 p-2 bg-gray-50 rounded text-sm text-gray-600">
                  الأب الحالي: {currentParentLineage}
                </div>
              )}
              <MemberSearch
                onSelect={(memberId) => setFormData(prev => ({ ...prev, parentId: memberId }))}
                placeholder="ابحث عن الأب..."
                className="w-full"
                excludeId={formData.parentId}
              />
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button
            onClick={handleSubmit}
            className="hover:bg-secondary"
            disabled={!formData.childName || !formData.parentId}
          >
            قبول الطلب
          </Button>
          <Button 
            type="button" 
            variant="outline" 
            onClick={onClose}
          >
            إلغاء
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}